'use client';

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ProcessorEditHeader } from '../../components/processor-edit-header';

import { ProcessorDetails, useProcessorById, useUpdateProcessor } from '@/lib/api/processors';
import { ProcessorDryModeField } from '../../components/processor-dry-mode-field';
import { ErrorDisplay } from '@/components/ui/error-display';
import { parseTRPCError } from '@/lib/utils/trpc-error-utils';
import { useNavigate } from 'react-router-dom';
import { useProcessorEditId } from '@/app/processors/hooks/use-processor-edit-id';
import { useProcessorEditExit } from '@/app/processors/hooks/use-processor-edit-exit';
import { MastercardEditSkeleton } from '../components/mastercard-edit-skeleton';
import { useToast } from '@/components/ui/use-toast';
import { SecretInput } from '@/components/form/secret-input';

interface MastercardEditPageProps {
  processor: ProcessorDetails;
}

const MastercardEditPageContainerInner = () => {
  const { t } = useTranslation('processors');
  const id = useProcessorEditId();
  const { data, isLoading, error } = useProcessorById(id);

  if (isLoading) {
    return <MastercardEditSkeleton />;
  }

  if (error) {
    return <ErrorDisplay error={parseTRPCError(error)} />;
  }

  if (!data) {
    return <div className="container mx-auto py-8">{t('processorNotFound')}</div>;
  }

  return <MastercardEditPage processor={data} />;
};

const MastercardEditPage = ({ processor }: MastercardEditPageProps) => {
  const { t } = useTranslation('processors');
  const navigate = useNavigate();
  const { toast } = useToast();
  const processorExit = useProcessorEditExit();

  const [validationError, setValidationError] = useState<string | null>(null);

  const updateProcessorMutation = useUpdateProcessor();

  const [formData, setFormData] = useState({
    name: processor.name || '',
    enabled: processor.enabled ?? true,
    testMode: (processor.options as any)?.testMode ?? false,
    dryMode: (processor.options as any)?.dryMode === true,
    password: (processor.options as any)?.password || '',
    merchantId: (processor.options as any)?.merchantId || '',
    gatewayUrl: (processor.options as any)?.gatewayUrl || '',
    apiVersion: (processor.options as any)?.apiVersion || '',
    descriptor: (processor.options as any)?.descriptor?.descriptor || '',
    phone: (processor.options as any)?.descriptor?.phone || '',
    address: (processor.options as any)?.descriptor?.address || '',
    city: (processor.options as any)?.descriptor?.city || '',
    state: (processor.options as any)?.descriptor?.state || '',
    postal: (processor.options as any)?.descriptor?.postal || '',
    country: (processor.options as any)?.descriptor?.country || '',
  });

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.password) {
      setValidationError(t('passwordRequired'));
      return;
    }
    if (!formData.merchantId) {
      setValidationError(t('merchantIdRequired'));
      return;
    }
    if (!formData.gatewayUrl) {
      setValidationError(t('gatewayUrlRequired'));
      return;
    }

    setValidationError(null);

    try {
      await updateProcessorMutation.mutateAsync({
        processorId: processor.id,
        updatedData: {
          type: processor.type,
          name: formData.name,
          enabled: formData.enabled,
          options: {
            testMode: formData.testMode,
            dryMode: formData.dryMode,
            password: formData.password,
            merchantId: formData.merchantId,
            gatewayUrl: formData.gatewayUrl,
            apiVersion: formData.apiVersion,
            descriptor: {
              descriptor: formData.descriptor,
              phone: formData.phone,
              address: formData.address,
              city: formData.city,
              state: formData.state,
              postal: formData.postal,
              country: formData.country,
            },
          },
        } as any,
      });

      toast({ description: t('processorUpdated') });
      if (processorExit.isEmbed) {
        processorExit.exitAfterSave();
        return;
      }
      navigate(-1);
    } catch (error) {
      console.error('Error saving processor:', error);
      toast({ description: t('failedToSave'), variant: 'destructive' });
    }
  };

  const handleRegionPreset = (region: string) => {
    const gatewayUrls: Record<string, string> = {
      nam: 'https://rakbankpay-nam.gateway.mastercard.com',
      eu: 'https://rakbankpay-eu.gateway.mastercard.com',
      ap: 'https://rakbankpay-ap.gateway.mastercard.com',
      uat: 'https://rakbankpay-uat.gateway.mastercard.com',
    };

    if (gatewayUrls[region]) {
      setFormData({ ...formData, gatewayUrl: gatewayUrls[region] });
    }
  };

  return (
    <div className="container mx-auto py-8">
      <ProcessorEditHeader
        processorId={processor.id}
        title={t('editMastercardConfiguration')}
        onCancel={() => (processorExit.isEmbed ? processorExit.exitCancel() : navigate(-1))}
        onSave={handleSubmit}
        isSaving={updateProcessorMutation.isPending}
      />

      {validationError && (
        <Alert variant="destructive" className="mb-6">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        {/* General Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('generalSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">{t('name')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('enterProcessorName')}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="enabled"
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked === true })}
              />
              <Label htmlFor="enabled">{t('enabled')}</Label>
            </div>
          </CardContent>
        </Card>

        {/* API Credentials */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('apiCredentials')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground text-sm">{t('mastercardCredentialsDescription')}</p>

            <div>
              <SecretInput
                label={`${t('password')} *`}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e })}
                placeholder={t('enterMastercardPassword')}
              />
            </div>

            <div>
              <Label htmlFor="merchantId">{t('merchantId')} *</Label>
              <Input
                id="merchantId"
                value={formData.merchantId}
                onChange={(e) => setFormData({ ...formData, merchantId: e.target.value })}
                placeholder={t('enterMerchantId')}
              />
            </div>

            <div>
              <Label htmlFor="regionPreset">{t('rakBankPayRegionPreset')}</Label>
              <Select onValueChange={handleRegionPreset}>
                <SelectTrigger>
                  <SelectValue placeholder={t('selectRakBankPayRegion')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nam">{t('rakBankPayNAM')}</SelectItem>
                  <SelectItem value="eu">{t('rakBankPayEU')}</SelectItem>
                  <SelectItem value="ap">{t('rakBankPayAP')}</SelectItem>
                  <SelectItem value="uat">{t('rakBankPayUAT')}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-muted-foreground mt-1 text-xs">{t('rakBankPayRegionDescription')}</p>
            </div>

            <div>
              <Label htmlFor="gatewayUrl">{t('gatewayUrl')} *</Label>
              <Input
                id="gatewayUrl"
                type="url"
                value={formData.gatewayUrl}
                onChange={(e) => setFormData({ ...formData, gatewayUrl: e.target.value })}
                placeholder="https://rakbankpay-nam.gateway.mastercard.com"
              />
              <p className="text-muted-foreground mt-1 text-xs">{t('gatewayUrlDescription')}</p>
            </div>

            <div>
              <Label htmlFor="apiVersion">{t('apiVersion')}</Label>
              <Input
                id="apiVersion"
                value={formData.apiVersion}
                onChange={(e) => setFormData({ ...formData, apiVersion: e.target.value })}
                placeholder="72"
              />
              <p className="text-muted-foreground mt-1 text-xs">{t('apiVersionDescription')}</p>
            </div>

            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="text-sm font-medium text-blue-700">{t('howToGetCredentials')}</p>
              <ol className="mt-2 list-decimal space-y-1 pl-4 text-sm text-blue-700">
                <li>{t('credentialStep1')}</li>
                <li>{t('credentialStep2')}</li>
                <li>{t('credentialStep3')}</li>
                <li>{t('credentialStep4')}</li>
                <li>{t('credentialStep5')}</li>
              </ol>
            </div>
          </CardContent>
        </Card>

        {/* Test Mode Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('testModeSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="testMode"
                checked={formData.testMode}
                onCheckedChange={(checked) => setFormData({ ...formData, testMode: checked === true })}
              />
              <Label htmlFor="testMode">{t('enableTestMode')}</Label>
            </div>
            {formData.testMode && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                <p className="text-sm text-yellow-800">
                  {t('mastercardTestModeDescription')}{' '}
                  <a
                    href="https://test-gateway.mastercard.com/api/documentation/integrationGuidelines/index.html?locale=en_US"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-yellow-900 underline hover:text-yellow-950"
                  >
                    {t('viewMastercardTestDocs')}
                  </a>
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Descriptor Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('descriptorSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground text-sm">{t('mastercardDescriptorDescription')}</p>

            <div>
              <Label htmlFor="descriptor">{t('statementDescriptor')}</Label>
              <Input
                id="descriptor"
                value={formData.descriptor}
                onChange={(e) => setFormData({ ...formData, descriptor: e.target.value })}
                placeholder="YOURCOMPANY.COM"
                maxLength={25}
              />
              <p className="text-muted-foreground mt-1 text-xs">{t('statementDescriptorDescription')}</p>
            </div>

            <div>
              <Label htmlFor="phone">{t('phone')}</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+1-555-123-4567"
              />
              <p className="text-muted-foreground mt-1 text-xs">{t('phoneDescription')}</p>
            </div>

            <div>
              <Label htmlFor="address">{t('address')}</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main St"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">{t('city')}</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="New York"
                />
              </div>

              <div>
                <Label htmlFor="state">{t('state')}</Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  placeholder="NY"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="postal">{t('postalCode')}</Label>
                <Input
                  id="postal"
                  value={formData.postal}
                  onChange={(e) => setFormData({ ...formData, postal: e.target.value })}
                  placeholder="10001"
                />
              </div>

              <div>
                <Label htmlFor="country">{t('country')}</Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  placeholder="US"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Supported Features */}
        <Card>
          <CardHeader>
            <CardTitle>{t('supportedFeatures')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="text-muted-foreground list-disc space-y-1 pl-4 text-sm">
              <li>{t('globalPaymentProcessing')}</li>
              <li>{t('multiCurrencySupport')}</li>
              <li>
                {t('paymentMethodSupport')}
                <ul className="mt-1 list-disc pl-4">
                  <li>{t('majorCreditCards')}</li>
                  <li>{t('localPaymentMethods')}</li>
                </ul>
              </li>
              <li>{t('threeDSecureAuth')}</li>
              <li>{t('recurringPaymentsStoredCreds')}</li>
              <li>{t('authCaptureWorkflows')}</li>
              <li>{t('comprehensiveFraudDetection')}</li>
              <li>{t('realtimeTransactionMonitoring')}</li>
            </ul>
          </CardContent>
        </Card>

                <ProcessorDryModeField
                    checked={formData.dryMode}
                    onCheckedChange={(checked) => setFormData({ ...formData, dryMode: checked })}
                    disabled={updateProcessorMutation.isPending}
                />
      </div>
    </div>
  );
};

export const MastercardEditPageContainer = withRequiredPermission(MastercardEditPageContainerInner, PERMISSIONS.PROCESSORS_MANAGE);
